'use client';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { Search } from 'lucide-react';
import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import type { ConceptMetadata } from '../../constants/concept-index';

import { Navigation } from '../../components/navigation';
import SimpleFooter from '../../components/simple-footer';
import StatsSection from '../../components/stats-section';
import { conceptsByLetter } from '../../constants/concept-index';
import { getConceptIcon } from '../../utils/concept-icons';

const DIFFICULTY_COLORS = {
  Advanced: 'text-semantic-error',
  Beginner: 'text-semantic-success',
  Intermediate: 'text-semantic-warning',
} as const;

const ConceptIcon = memo(({ concept }: { concept: ConceptMetadata }) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const colorClass = DIFFICULTY_COLORS[concept.difficulty] || 'text-text-tertiary';
  const IconComponent = useMemo(() => getConceptIcon(concept), [concept]);

  if (!isClient) {
    return (
      <div className={`w-6 h-6 flex items-center justify-center ${colorClass} relative z-10`} />
    );
  }

  return (
    <div className={`w-6 h-6 flex items-center justify-center ${colorClass} relative z-10`}>
      <IconComponent className="w-full h-full" />
    </div>
  );
});

ConceptIcon.displayName = 'ConceptIcon';

const ConceptCard = memo(
  ({ concept, index }: { concept: ConceptMetadata; index: number }) => {
    const { trackEvent } = useInteractionTracker();
    const animationDelay = `${Math.min(index * 50, 500)}ms`;

    return (
      <Link
        aria-label={`Learn about ${concept.name} - ${concept.difficulty} level`}
        className="h-full flex flex-col items-center gap-2 p-3 glass-card hover:glass-subtle border border-glass rounded-xl card-hover-lift transition-all duration-300 group relative cursor-pointer focus-ring-primary"
        href={`/hotmath_help/topics/${concept.id}`}
        onClick={() =>
          void trackEvent({ action: 'click-concept-link', category: 'page', label: `concept-${concept.id}` })
        }
        style={{ animationDelay }}
      >
        <div className="absolute left-0 top-0 w-full h-full bg-gradient-radial from-brand-primary to-transparent blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300" />
        <ConceptIcon concept={concept} />
        <span className="text-secondary text-base text-center relative group-hover:text-primary transition-colors duration-300 font-normal">
          {concept.name}
        </span>
      </Link>
    );
  }
);

ConceptCard.displayName = 'ConceptCard';

const LetterSection = memo(
  ({ concepts, letter }: { letter: string; concepts: ConceptMetadata[] }) => (
    <section className="flex flex-col" id={`section-${letter}`}>
      <h2 className="text-xl font-semibold text-primary mb-4">
        {letter === '#' ? 'Numbers' : `${letter} Concepts`}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
        {concepts.map((concept, index) => (
          <ConceptCard concept={concept} index={index} key={concept.id} />
        ))}
      </div>
    </section>
  )
);

LetterSection.displayName = 'LetterSection';

export default function HomePageClient() {
  const [searchInput, setSearchInput] = useState('');
  const { trackEvent } = useInteractionTracker();

  const filteredConcepts = useMemo(() => {
    if (!searchInput.trim()) {
      return conceptsByLetter;
    }

    const searchTerm = searchInput.toLowerCase();
    const filtered: Record<string, ConceptMetadata[]> = {};

    Object.entries(conceptsByLetter).forEach(([letter, concepts]) => {
      const matchingConcepts = concepts.filter(concept =>
        concept.name.toLowerCase().includes(searchTerm)
      );

      if (matchingConcepts.length > 0) {
        filtered[letter] = matchingConcepts;
      }
    });

    return filtered;
  }, [searchInput]);

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      const length = value?.length ?? 0;
      void trackEvent({ action: 'set-search', category: 'page', label: `search-query-length-${length}` });
    },
    [trackEvent]
  );

  const totalResults = useMemo(
    () => Object.values(filteredConcepts).reduce((sum, concepts) => sum + concepts.length, 0),
    [filteredConcepts]
  );

  const availableLetters = useMemo(
    () =>
      Object.keys(conceptsByLetter).sort((a, b) => {
        if (a === '#') return -1;
        if (b === '#') return 1;
        return a.localeCompare(b);
      }),
    []
  );

  return (
    <div className="min-h-screen flex flex-col bg-surface-primary">
      <Navigation />

      <main className="flex-1" id="main-content">
        <div className="container mx-auto max-w-7xl px-4 py-4 md:px-6 md:py-6">
          <header className="text-center mb-12 mt-8">
            <div className="mb-6">
              <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight text-primary mb-4">
                <span className="relative inline-block">
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 py-1.5 gradient-text-hero blur-lg opacity-50 dark:opacity-75 before:content-[attr(data-text)]"
                    data-text="HotMath"
                  />
                  <span className="relative py-1.5 gradient-text-hero left-0 top-0">
                    HotMath
                  </span>
                </span>{' '}
                <span className="neon-glow">Adventures!</span>
              </h1>
              <p className="text-lg lg:text-xl text-secondary max-w-4xl mx-auto leading-relaxed">
                Unlock the secrets of math! Choose your adventure and become a
                math superhero!
              </p>
            </div>

            <StatsSection />
          </header>

          <section className="mb-8" role="search">
            <div className="md:max-w-lg md:mx-auto relative">
              <Search
                aria-hidden="true"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-secondary z-10 pointer-events-none"
                size={24}
                strokeWidth={2}
              />
              <input
                aria-label="Search math concepts"
                className="w-full pl-12 pr-4 py-3 glass-card border border-glass rounded-xl text-primary placeholder-secondary focus-ring-primary transition-all duration-200"
                id="search"
                onChange={e => onSearchChange(e.target.value)}
                placeholder="Search for math concepts..."
                type="search"
                value={searchInput}
              />
            </div>
            {searchInput && totalResults > 0 && (
              <p aria-live="polite" className="text-secondary text-sm" role="status">
                {`Found ${totalResults} concept${totalResults !== 1 ? 's' : ''} matching "${searchInput}"`}
              </p>
            )}
          </section>

          {!searchInput && (
            <nav aria-label="Jump to letter section" className="mb-8" id="letter-navigation">
              <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
                {availableLetters.map(letter => (
                  <a
                    aria-label={`Jump to ${letter === '#' ? 'Numbers' : letter} concepts`}
                    className="px-3 py-2 glass-card border border-glass rounded-lg text-secondary hover:bg-brand-secondary/10 hover:text-brand-secondary hover:border-brand-secondary/30 transition-all duration-200 font-medium text-sm focus-ring-primary"
                    href={`#section-${letter}`}
                    key={letter}
                    onClick={() => void trackEvent({ action: 'click-letter', category: 'page', label: `${letter}` })}
                  >
                    {letter}
                  </a>
                ))}
              </div>
            </nav>
          )}

          <section className="space-y-8">
            {Object.entries(filteredConcepts).map(([letter, concepts]) => (
              <LetterSection concepts={concepts} key={letter} letter={letter} />
            ))}
          </section>

          {searchInput && totalResults === 0 && (
            <div className="text-center my-12" role="status">
              <p className="text-secondary mb-4">No concepts found for "{searchInput}"</p>
              <p className="text-tertiary text-sm">
                Try searching with different keywords or browse by letter above.
              </p>
            </div>
          )}
        </div>
      </main>

      <SimpleFooter />
    </div>
  );
}


